import { useEffect, useRef, useCallback } from 'react';
import { useTradingStore } from '../stores/tradingStore';
import { useAuthStore } from '../stores/authStore';
import type { WebSocketMessage } from '../types';

const WS_BASE_URL = import.meta.env.VITE_WS_URL || `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws/live`;

export function useWebSocket() {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { setConnected, updateFromWebSocket, addSignal } = useTradingStore();
  const { accessToken, isAuthenticated } = useAuthStore();

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    // SECURITY: Don't connect if not authenticated
    if (!isAuthenticated && !accessToken) {
      console.log('WebSocket: Not authenticated, skipping connection');
      return;
    }

    try {
      // Append token as query parameter for authentication
      const wsUrl = accessToken
        ? `${WS_BASE_URL}?token=${encodeURIComponent(accessToken)}`
        : WS_BASE_URL;
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('WebSocket connected');
        setConnected(true);
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);

          switch (message.type) {
            case 'initial':
            case 'update':
              if (message.data) {
                updateFromWebSocket(message.data);
              }
              break;

            case 'signal':
              if (message.data?.signal) {
                addSignal(message.data.signal);
              }
              break;

            case 'heartbeat':
              // Send pong
              ws.send(JSON.stringify({ type: 'ping' }));
              break;

            case 'position_closed':
            case 'trade_executed':
              // These will trigger a full update via the next update message
              break;
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setConnected(false);

        // Reconnect after 3 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, 3000);
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnected(false);
      };

      wsRef.current = ws;
    } catch (e) {
      console.error('Failed to connect WebSocket:', e);
      setConnected(false);

      // Retry connection
      reconnectTimeoutRef.current = setTimeout(() => {
        connect();
      }, 5000);
    }
  }, [setConnected, updateFromWebSocket, addSignal, accessToken, isAuthenticated]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setConnected(false);
  }, [setConnected]);

  const sendMessage = useCallback((message: object) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  useEffect(() => {
    connect();

    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  return { connect, disconnect, sendMessage };
}
